import { type CSSProperties } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconPackageVerification from '@/icons/PackageVerification.svg';
import IconPlace from '@/icons/Place.svg';
import IconTruck from '@/icons/Truck.svg';
import IconWarning from '@/icons/Warning.svg';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { ArticleSubLocation } from './ArticleSubLocation';

import './ArticleLocation.css';

const { block, element } = createBEMClasses('article-location');

export const ArticleLocation = () => {
  const { article } = useArticleContext();
  const { isMobile } = useViewPort();

  const isInTransit = !!(article.inTransit || article.inTransitVerification);

  if (!isInTransit && !article.atEntity) return null;

  const location = isInTransit
    ? { from: article.fromEntity?.name, to: article.toEntity?.name }
    : article.atEntity
      ? { name: article.atEntity.name }
      : undefined;

  return (
    <Stack
      className={block({ 'in-transit': isInTransit && !isMobile })}
      row={isMobile}
      justifyContent={isMobile ? 'space-between' : undefined}
      alignItems={isMobile ? 'center' : 'flex-start'}
    >
      <ArticleLocationTitle
        isInTransit={isInTransit}
        isInTransitVerification={article.inTransitVerification}
        hasIssue={article.hasIssue}
        isMobile={isMobile}
        isNonDigitalized={!!article.atEntity?.workshopConfig?.nonDigitalized}
      />

      <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
        {!isInTransit && <IconPlace />}
        <ArticleLocationLabel
          location={location}
          isMobile={isMobile}
          isInTransitVerification={article.inTransitVerification}
          hasIssue={article.hasIssue}
        />
      </Stack>

      <ArticleSubLocation />
    </Stack>
  );
};

const InTransitLabel = ({
  gap = '0.25rem',
  iconClassName,
  iconStyle,
  className,
  ariaLabel,
  isInTransitVerification,
  hasIssue,
}: {
  gap?: string;
  iconClassName?: string;
  iconStyle?: CSSProperties;
  className?: string;
  ariaLabel?: string;
  isInTransitVerification: boolean;
  hasIssue: boolean;
}) => (
  <Stack
    row
    gap={gap}
    alignItems="center"
    flexWrap="nowrap"
    className={className}
    ariaLabel={ariaLabel}
  >
    {hasIssue ? (
      <Tooltip
        content={
          <Trans id="components.request-location.shipment-issue">
            A shipment issue has been reported
          </Trans>
        }
      >
        <Button variant="style-less">
          <IconWarning
            className={iconClassName}
            style={{ ...iconStyle, color: 'var(--color-warning-700)' }}
          />
        </Button>
      </Tooltip>
    ) : isInTransitVerification ? (
      <IconPackageVerification className={iconClassName} style={iconStyle} />
    ) : (
      <IconTruck className={iconClassName} style={iconStyle} />
    )}
    {isInTransitVerification ? (
      <InlineText weight="medium" color={hasIssue ? 'warning' : undefined}>
        <Trans id="components.request-location.in-transit-verification">Package verification</Trans>
      </InlineText>
    ) : (
      <InlineText weight="medium">
        <Trans id="components.request-location.in-transit">In transit</Trans>
      </InlineText>
    )}
  </Stack>
);

const ArticleLocationTitle = ({
  isInTransit,
  isInTransitVerification,
  hasIssue,
  isMobile,
  isNonDigitalized,
}: {
  isInTransit: boolean;
  isInTransitVerification: boolean;
  hasIssue: boolean;
  isMobile: boolean;
  isNonDigitalized: boolean;
}) => {
  if (isInTransit && !isMobile) {
    return (
      <InTransitLabel
        gap="0.5rem"
        iconClassName={element('in-transit-icon')}
        className={element('in-transit-header')}
        isInTransitVerification={isInTransitVerification}
        hasIssue={hasIssue}
      />
    );
  }

  return (
    <Paragraph color="secondary" size={isMobile ? '100' : '200'}>
      {isNonDigitalized ? (
        <Trans id="components.request-location.expected-current-location">
          Expected current location
        </Trans>
      ) : (
        <Trans id="components.request-location.current-location">Current location</Trans>
      )}
    </Paragraph>
  );
};

type Location = { from: string | undefined; to: string | undefined } | { name: string } | undefined;

const ArticleLocationLabel = ({
  location,
  isMobile,
  isInTransitVerification,
  hasIssue,
}: {
  location: Location;
  isMobile: boolean;
  isInTransitVerification: boolean;
  hasIssue: boolean;
}) => {
  const { t } = useLingui();

  if (!location) return null;

  const currentLocationLabel = t({
    id: 'components.request-location.current-location',
    message: 'Current location',
  });

  if ('from' in location) {
    if (!isMobile) {
      return (
        <Stack row gap="0.25rem" alignItems="center" ariaLabel={currentLocationLabel}>
          <InlineText
            aria-label={t({
              id: 'components.request-location.aria.from-location',
              message: 'From location',
            })}
            className={element('transit-from')}
            size="200"
          >
            {location.from}
          </InlineText>
          <InlineText size="200">→</InlineText>
          <InlineText
            aria-label={t({
              id: 'components.request-location.aria.to-location',
              message: 'To location',
            })}
            className={element('transit-to')}
            size="200"
          >
            {location.to}
          </InlineText>
        </Stack>
      );
    }

    return (
      <InTransitLabel
        ariaLabel={currentLocationLabel}
        iconStyle={{ fontSize: '1.2rem' }}
        isInTransitVerification={isInTransitVerification}
        hasIssue={hasIssue}
      />
    );
  }

  return (
    <Paragraph aria-label={currentLocationLabel} size="100" weight="medium">
      {location.name}
    </Paragraph>
  );
};
